import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "../../../api/axiosInstance";

const BASE = "dashboard";

/**
 * Upload and run a PHP migration file
 * @param {FormData} formData - formData with key "migration" (PHP file)
 */
export const runMigrationFunc = createAsyncThunk(
  "migrationSeederSlicer/runMigrationFunc",
  async (formData, thunkAPI) => {
    const { rejectWithValue } = thunkAPI;
    try {
      const response = await api.post(`${BASE}/run-migration`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return { type: "migration", ...response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Upload and run a PHP seeder file
 * @param {FormData} formData - formData with "seeder" (PHP file), optional "seeder_class"
 */
export const runSeederFunc = createAsyncThunk(
  "migrationSeederSlicer/runSeederFunc",
  async (formData, thunkAPI) => {
    const { rejectWithValue } = thunkAPI;
    try {
      const response = await api.post(`${BASE}/run-seeder`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return { type: "seeder", ...response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Run existing seeder by class name
 * @param {string} className - e.g. "Database\\Seeders\\PermissionSeeder"
 */
export const runSeederByClassFunc = createAsyncThunk(
  "migrationSeederSlicer/runSeederByClassFunc",
  async (className, thunkAPI) => {
    const { rejectWithValue } = thunkAPI;
    try {
      const response = await api.post(`${BASE}/run-seeder-by-class`, {
        class: className,
      });
      return { type: "seederByClass", ...response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const migrationSeederSlicer = createSlice({
  name: "migrationSeederSlicer",
  initialState: {
    migration: { loading: false, output: null, error: null },
    seeder: { loading: false, output: null, error: null },
    seederByClass: { loading: false, output: null, error: null },
  },
  reducers: {
    clearMigrationOutput: (state) => {
      state.migration.output = null;
      state.migration.error = null;
    },
    clearSeederOutput: (state) => {
      state.seeder.output = null;
      state.seeder.error = null;
    },
    clearSeederByClassOutput: (state) => {
      state.seederByClass.output = null;
      state.seederByClass.error = null;
    },
  },
  extraReducers: (builder) => {
    // Run Migration
    builder
      .addCase(runMigrationFunc.pending, (state) => {
        state.migration.loading = true;
        state.migration.error = null;
        state.migration.output = null;
      })
      .addCase(runMigrationFunc.fulfilled, (state, action) => {
        state.migration.loading = false;
        state.migration.output = action.payload?.data?.output ?? action.payload?.message ?? "";
        state.migration.error = null;
      })
      .addCase(runMigrationFunc.rejected, (state, action) => {
        state.migration.loading = false;
        state.migration.error = action.payload?.message || action.payload?.errors || "Migration failed.";
        state.migration.output = null;
      });
    // Run Seeder (upload)
    builder
      .addCase(runSeederFunc.pending, (state) => {
        state.seeder.loading = true;
        state.seeder.error = null;
        state.seeder.output = null;
      })
      .addCase(runSeederFunc.fulfilled, (state, action) => {
        state.seeder.loading = false;
        state.seeder.output = action.payload?.data?.output ?? action.payload?.message ?? "";
        state.seeder.error = null;
      })
      .addCase(runSeederFunc.rejected, (state, action) => {
        state.seeder.loading = false;
        state.seeder.error = action.payload?.message || action.payload?.errors || "Seeder failed.";
        state.seeder.output = null;
      });
    // Run Seeder by class
    builder
      .addCase(runSeederByClassFunc.pending, (state) => {
        state.seederByClass.loading = true;
        state.seederByClass.error = null;
        state.seederByClass.output = null;
      })
      .addCase(runSeederByClassFunc.fulfilled, (state, action) => {
        state.seederByClass.loading = false;
        state.seederByClass.output = action.payload?.data?.output ?? action.payload?.message ?? "";
        state.seederByClass.error = null;
      })
      .addCase(runSeederByClassFunc.rejected, (state, action) => {
        state.seederByClass.loading = false;
        state.seederByClass.error = action.payload?.message || action.payload?.errors || "Seeder failed.";
        state.seederByClass.output = null;
      });
  },
});

export const {
  clearMigrationOutput,
  clearSeederOutput,
  clearSeederByClassOutput,
} = migrationSeederSlicer.actions;
export default migrationSeederSlicer.reducer;
